import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { venuesAPI, mediaAPI } from '../../services/api';
import {
    Info,
    MapPin,
    Settings,
    CheckCircle,
    ArrowLeft,
    ArrowRight
} from 'lucide-react';
import './VenueCreationWizard.css';

import BasicInfoStep from './steps/BasicInfoStep';
import LocationStep from './steps/LocationStep';
import DetailsStep from './steps/DetailsStep';
import ReviewStep from './steps/ReviewStep';

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

const VenueCreationWizard = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [imageUploadState, setImageUploadState] = useState({
        previewUrl: '',
        isUploading: false,
        error: '',
        fileName: '',
        fileSize: 0
    });
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const [formData, setFormData] = useState({
        // Basic Information
        name: '',
        description: '',
        type: '',
        capacity: '',
        imageUrl: '',

        // Location Information
        street: '',
        city: '',
        state: '',
        country: '',
        zipCode: '',
        placeId: '',
        formattedAddress: '',
        addressId: null,

        // Contact Information
        phone: '',
        email: '',
        website: '',

        // Amenities
        amenities: [],
        hasBalcony: false,
        hasVipSection: false,
        accessibilityFeatures: []
    });

    const venueTypes = [
        'Theater', 'Concert Hall', 'Sports Arena', 'Conference Center',
        'Auditorium', 'Amphitheater', 'Stadium', 'Convention Center',
        'Opera House', 'Cultural Center', 'Art Gallery', 'Museum', 'Club'
    ];

    const amenityOptions = [
        'Parking', 'Accessible Seating', 'Concessions', 'Gift Shop',
        'Restrooms', 'VIP Lounge', 'Coat Check', 'Security',
        'Wi-Fi', 'Air Conditioning', 'Audio/Visual Equipment',
        'Catering Kitchen', 'Green Room', 'Orchestra Pit'
    ];

    const accessibilityOptions = [
        'Wheelchair Access', 'Hearing Loop', 'Sign Language Interpreters',
        'Large Print Programs', 'Audio Descriptions', 'Accessible Parking',
        'Elevator Access', 'Accessible Restrooms', 'Braille Signage',
        'Assisted Listening Devices'
    ];

    const steps = [
        { number: 1, label: 'Basic Info', icon: <Info size={20} /> },
        { number: 2, label: 'Location', icon: <MapPin size={20} /> },
        { number: 3, label: 'Details', icon: <Settings size={20} /> },
        { number: 4, label: 'Review', icon: <CheckCircle size={20} /> }
    ];

    useEffect(() => {
        const loadVenue = async () => {
            if (!isEdit) return;
            setLoading(true);
            try {
                const resp = await venuesAPI.getById(id);
                const venue = resp?.data?.data || resp?.data || resp;
                if (venue) {
                    setFormData({
                        name: venue.name || '',
                        description: venue.description || '',
                        type: venue.venueType || '',
                        capacity: venue.capacity != null ? String(venue.capacity) : '',
                        street: venue.address?.streetAddress || venue.address?.formattedAddress || '',
                        city: venue.address?.city || '',
                        state: venue.address?.stateProvince || '',
                        country: venue.address?.country || '',
                        zipCode: venue.address?.postalCode || '',
                        placeId: venue.address?.placeId || '',
                        formattedAddress: venue.address?.formattedAddress || '',
                        addressId: venue.address?.id || null,
                        phone: venue.phone || '',
                        email: venue.email || '',
                        website: venue.website || '',
                        amenities: venue.amenities || [],
                        hasBalcony: Boolean(venue.hasBalcony),
                        hasVipSection: Boolean(venue.hasVipSection),
                        accessibilityFeatures: venue.accessibilityFeatures || [],
                        imageUrl: venue.imageUrl || ''
                    });
                    setSuccessMessage('Venue loaded. You can now update the details.');
                }
            } catch (err) {
                console.error('Failed to load venue for editing:', err);
                setErrors({ submit: 'Could not load venue details. Please try again.' });
            } finally {
                setLoading(false);
            }
        };

        loadVenue();
    }, [id, isEdit]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleArrayChange = (field, value, checked) => {
        setFormData(prev => ({
            ...prev,
            [field]: checked
                ? [...prev[field], value]
                : prev[field].filter(item => item !== value)
        }));
    };

    const handlePlaceSelected = (place) => {
        if (!place) return;

        // The GooglePlacesAutocomplete component returns a processed object, not the raw Google result
        setFormData(prev => ({
            ...prev,
            street: place.streetAddress || prev.street,
            city: place.city || prev.city,
            state: place.state || prev.state,
            country: place.country || prev.country,
            zipCode: place.zipCode || prev.zipCode,
            placeId: place.placeId || prev.placeId,
            formattedAddress: place.formattedAddress || prev.formattedAddress
        }));

        // Clear location errors
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.street;
            delete newErrors.city;
            delete newErrors.state;
            delete newErrors.country;
            return newErrors;
        });
    };

    const handleImageUpload = async (file) => {
        if (!file) return;
        if (file.size > MAX_IMAGE_SIZE_BYTES) {
            setImageUploadState(prev => ({ ...prev, error: 'File too large (max 5MB)' }));
            return;
        }

        setImageUploadState(prev => ({
            ...prev,
            isUploading: true,
            error: '',
            fileName: file.name,
            fileSize: file.size
        }));

        try {
            const response = await mediaAPI.uploadEventImage(file);
            setFormData(prev => ({ ...prev, imageUrl: response.url }));
            setImageUploadState(prev => ({
                ...prev,
                isUploading: false,
                previewUrl: URL.createObjectURL(file)
            }));
            setSuccessMessage('Image uploaded!');
        } catch (err) {
            console.error('Upload failed', err);
            setImageUploadState(prev => ({ ...prev, isUploading: false, error: 'Upload failed' }));
        }
    };

    const validateStep = (step) => {
        const newErrors = {};
        let isValid = true;

        switch (step) {
            case 1:
                if (!formData.name.trim()) newErrors.name = 'Venue name is required';
                if (formData.name.length > 255) newErrors.name = 'Venue name too long';
                if (!formData.description.trim()) newErrors.description = 'Description is required';
                if (formData.description.length > 1000) newErrors.description = 'Description too long';
                if (!formData.type) newErrors.type = 'Venue type is required';
                if (!formData.capacity || parseInt(formData.capacity) < 1) newErrors.capacity = 'Valid capacity required';
                break;
            case 2:
                if (!formData.street.trim()) newErrors.street = 'Street address is required';
                if (!formData.city.trim()) newErrors.city = 'City is required';
                if (!formData.state.trim()) newErrors.state = 'State/Province is required';
                if (!formData.country.trim()) newErrors.country = 'Country is required';
                break;
            case 3:
                if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
                if (!formData.email.trim()) newErrors.email = 'Email is required';
                if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email';
                break;
            default:
                break;
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            isValid = false;
        }
        return isValid;
    };

    const nextStep = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(prev => prev + 1);
            window.scrollTo(0, 0);
        }
    };

    const prevStep = () => {
        setCurrentStep(prev => prev - 1);
        window.scrollTo(0, 0);
    };

    const handleSubmit = async () => {
        setLoading(true);
        setErrors({});

        const truncateField = (value, maxLength) => {
            if (value && typeof value === 'string' && value.length > maxLength) {
                return value.substring(0, maxLength);
            }
            return value;
        };

        try {
            const payload = {
                name: truncateField(formData.name, 255),
                description: truncateField(formData.description, 1000),
                venueType: formData.type,
                capacity: parseInt(formData.capacity, 10),
                phone: truncateField(formData.phone, 50),
                email: truncateField(formData.email, 255),
                website: truncateField(formData.website, 255),
                imageUrl: formData.imageUrl,
                amenities: formData.amenities,
                hasBalcony: formData.hasBalcony,
                hasVipSection: formData.hasVipSection,
                accessibilityFeatures: formData.accessibilityFeatures,
            };

            const isValidPlaceId = formData.placeId && formData.placeId.trim() !== '';

            if (isEdit) {
                payload.address = {
                    streetAddress: formData.street,
                    city: formData.city,
                    stateProvince: formData.state,
                    postalCode: formData.zipCode,
                    country: formData.country,
                    formattedAddress: formData.formattedAddress,
                    placeId: formData.placeId,
                    latitude: 0.0,
                    longitude: 0.0,
                    id: formData.addressId
                };
                await venuesAPI.update(id, payload);
                setSuccessMessage('Venue updated!');
            } else {
                if (isValidPlaceId) {
                    await venuesAPI.createWithPlaceId(payload, formData.placeId);
                } else {
                    payload.address = {
                        streetAddress: formData.street,
                        city: formData.city,
                        stateProvince: formData.state,
                        postalCode: formData.zipCode,
                        country: formData.country,
                        formattedAddress: formData.formattedAddress,
                        placeId: formData.placeId,
                        latitude: 0.0,
                        longitude: 0.0
                    };
                    await venuesAPI.create(payload);
                }
                setSuccessMessage('Venue created!');
            }

            setTimeout(() => navigate('/dashboard/venues'), 1000);

        } catch (err) {
            console.error('Submit failed', err);
            setErrors({ submit: 'Failed to save venue.' });
        } finally {
            setLoading(false);
        }
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <BasicInfoStep
                        formData={formData}
                        errors={errors}
                        venueTypes={venueTypes}
                        handleInputChange={handleInputChange}
                    />
                );
            case 2:
                return (
                    <LocationStep
                        formData={formData}
                        errors={errors}
                        handleInputChange={handleInputChange}
                        handlePlaceSelected={handlePlaceSelected}
                    />
                );
            case 3:
                return (
                    <DetailsStep
                        formData={formData}
                        errors={errors}
                        handleInputChange={handleInputChange}
                        handleArrayChange={handleArrayChange}
                        onImageUpload={handleImageUpload}
                        imageUploadState={imageUploadState}
                        amenityOptions={amenityOptions}
                        accessibilityOptions={accessibilityOptions}
                    />
                );
            case 4:
                return (
                    <ReviewStep
                        formData={formData}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="wizard-container">
            <Helmet>
                <title>{isEdit ? 'Edit Venue' : 'Create Venue'} | TikPlat</title>
            </Helmet>

            <div className="wizard-content">
                <header className="wizard-header">
                    <h1>{isEdit ? 'Edit Venue' : 'Create New Venue'}</h1>
                    <p>
                        {isEdit
                            ? 'Update venue details and configuration.'
                            : 'Set up a new venue to start hosting events.'}
                    </p>
                </header>

                {successMessage && (
                    <div className="success-message">
                        <CheckCircle size={20} />
                        {successMessage}
                    </div>
                )}

                <div className="step-indicator">
                    {steps.map(step => (
                        <div
                            key={step.number}
                            className={`step ${currentStep === step.number ? 'active' : ''} ${currentStep > step.number ? 'completed' : ''}`}
                        >
                            <div className="step-icon-wrapper">
                                {step.icon}
                            </div>
                            <div className="step-label">{step.label}</div>
                        </div>
                    ))}
                </div>

                {renderStepContent()}

                <div className="action-buttons">
                    <button
                        className="back-btn"
                        onClick={prevStep}
                        disabled={currentStep === 1 || loading}
                    >
                        <ArrowLeft size={18} />
                        Back
                    </button>

                    {currentStep < steps.length ? (
                        <button
                            className="next-btn"
                            onClick={nextStep}
                            disabled={loading}
                        >
                            Next Step
                            <ArrowRight size={18} />
                        </button>
                    ) : (
                        <button
                            className="next-btn"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <div className="spinner" /> {isEdit ? 'Updating...' : 'Creating...'}
                                </>
                            ) : (
                                <>
                                    <CheckCircle size={18} />
                                    {isEdit ? 'Update Venue' : 'Create Venue'}
                                </>
                            )}
                        </button>
                    )}
                </div>
                {errors.submit && (
                    <div className="error-message" style={{ justifyContent: 'center', marginTop: '16px' }}>
                        {errors.submit}
                    </div>
                )}
            </div>
        </div>
    );
};

export default VenueCreationWizard;
